import React, { useEffect, useState } from "react";
import { Layout, Tooltip, Row, Col, Card, Button, Badge } from "antd";
import { Calendar } from "antd";
import {
  CheckCircleOutlined,
  HourglassOutlined,
  CloseCircleOutlined,
  FlagOutlined,
  RollbackOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { BOOKING_CALENDER } from "../../../../constants/api";
import { getAPI } from "../../../../utils/common";
import LayoutHeader from "../../../inc/LayoutHeader";
import LayoutFooter from "../../../inc/LayoutFooter";
import "./BookingCalendar.css"; // Add custom CSS for styling
import BookingStatusWidget from "./widgets/BookingStatusWidget"; // Import the widget

const BookingCalendar = () => {
  const { Content } = Layout;
  const [events, setEvents] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]); // State for available rooms
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // State for selected date
  const [bookingsForSelectedDate, setBookingsForSelectedDate] = useState([]); // Store bookings for the selected date

  const getData = () => {
    setLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        setEvents(data.data.filter((item) => item.booking_id)); // Filter for booked events
        setAvailableRooms(data.data.filter((item) => !item.booking_id)); // Filter for available rooms
        setLoading(false);
      } else {
        setEvents([]);
        setAvailableRooms([]);
        setLoading(false);
      }
    };
    const errorFn = function () {
      setEvents([]);
      setAvailableRooms([]);
      setLoading(false);
    };
    getAPI(BOOKING_CALENDER, successFn, errorFn, {});
  };

  useEffect(() => {
    getData();
  }, []);

  const handleDateCellRender = (value) => {
    const date = value.format("YYYY-MM-DD");

    // Filter events for the current date
    const eventsForDay = events.filter(
      (event) => event.start <= date && event.end >= date
    );

    // Filter available rooms for the current date
    const availableForDay = availableRooms.filter(
      (room) => room.start <= date && room.end >= date
    );

    return (
      <div className="event-list-container">
        {eventsForDay.length > 0 || availableForDay.length > 0 ? (
          <>
            {/* Display Booked Events */}
            {eventsForDay.length > 0 && (
              <ul>
                {eventsForDay.map((event) => (
                  <Badge.Ribbon
                    key={event.booking_id}
                    text={getStatusIcon(event.booking_status)}
                    color={getStatusColor(event.booking_status)}
                  >
                    <li className={`event-item status-${event.booking_status}`}>
                      <Tooltip
                        title={`Booking ID: ${event.booking_id}\nRoom: ${event.room_no}`}
                      >
                        <span className="event-title">{event.title}</span>
                      </Tooltip>
                    </li>
                  </Badge.Ribbon>
                ))}
              </ul>
            )}

            {/* Display Available Rooms */}
            {availableForDay.length > 0 && (
              <ul>
                {availableForDay.map((room) => (
                  <Badge.Ribbon
                    key={room.room_id}
                    text="Available"
                    color="#4caf50" // Green for available rooms
                  >
                    <li className="event-item available-room">
                      <Tooltip title={`Room: ${room.room_no}`}>
                        <span className="event-title">{room.title}</span>
                      </Tooltip>
                    </li>
                  </Badge.Ribbon>
                ))}
              </ul>
            )}
          </>
        ) : (
          <span className="no-events">No bookings or rooms available</span>
        )}
      </div>
    );
  };

  const handleDateClick = (value) => {
    const clickedDate = value.format("YYYY-MM-DD");
    setSelectedDate(clickedDate);

    // Filter events for the selected date
    const bookingsForThisDate = events.filter(
      (event) => event.start <= clickedDate && event.end >= clickedDate
    );
    setBookingsForSelectedDate(bookingsForThisDate);
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Row gutter={16}>
          <Col span={selectedDate ? 19 : 24}>
            <Card
              loading={loading}
              style={{ padding: "0px", margin: "0px" }}
              title={<BookingStatusWidget />}
            >
              <Calendar
                style={{ padding: "0px", margin: "0px" }}
                year
                dateCellRender={handleDateCellRender}
                onSelect={handleDateClick}
              />
            </Card>
          </Col>
          {selectedDate && (
            <Col span={5}>
              {/* Booking List Container */}
              <Card
                loading={loading}
                className="booking-list-container"
                title={`Bookings for ${selectedDate}`}
                extra={
                  <Button
                    style={{
                      marginTop: "5px",
                      marginBottom: "5px",
                    }}
                    onClick={() => setSelectedDate(null)}
                    type="primary"
                    ghost
                  >
                    <RollbackOutlined />
                  </Button>
                }
              >
                {bookingsForSelectedDate.length > 0 ? (
                  <ul>
                    {bookingsForSelectedDate.map((event) => (
                      <Badge.Ribbon
                        text={getStatusIcon(event.booking_status)}
                        color={getStatusColor(event.booking_status)}
                      >
                        <li
                          key={event.booking_id}
                          className={`event-item status-${event.booking_status}`}
                        >
                          <Tooltip
                            title={`Booking ID: ${event.booking_id}\nRoom: ${
                              event.room_no
                            }\nStatus: ${getStatusText(event.booking_status)}`}
                          >
                            <span className="event-title">
                              Booking ID: {event.booking_id}
                              <br />
                              Room: {event.title}
                            </span>
                          </Tooltip>
                        </li>
                      </Badge.Ribbon>
                    ))}
                  </ul>
                ) : (
                  <span className="no-events">No bookings for this date</span>
                )}
              </Card>

              {/* Available Rooms for the Selected Date */}
              {/* Available Rooms Container */}
              {selectedDate && (
                <Card
                  loading={loading}
                  className="available-rooms-container"
                  title={`Available Rooms for ${selectedDate}`}
                  extra={
                    <Button
                      style={{
                        marginTop: "5px",
                        marginBottom: "5px",
                      }}
                      onClick={() => setSelectedDate(null)}
                      type="primary"
                      ghost
                    >
                      <RollbackOutlined />
                    </Button>
                  }
                >
                  {availableRooms.length > 0 ? (
                    <ul>
                      {availableRooms
                        .filter(
                          (room) =>
                            room.start <= selectedDate &&
                            room.end >= selectedDate
                        )
                        .map((room) => (
                          <Badge.Ribbon
                            key={room.room_id}
                            text="Available"
                            color="#4caf50" // Green for available rooms
                          >
                            <li className="event-item available-room">
                              <Tooltip
                                title={`Room: ${room.title} (${room.room_no})`}
                              >
                                <span className="event-title">
                                  {`${room.title} (${room.room_no})`}
                                </span>
                              </Tooltip>
                            </li>
                          </Badge.Ribbon>
                        ))}
                    </ul>
                  ) : (
                    <span className="no-events">
                      No available rooms for this date
                    </span>
                  )}
                </Card>
              )}
            </Col>
          )}
        </Row>
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

export default BookingCalendar;

const getStatusIcon = (status) => {
  switch (status) {
    case "0":
      return <HistoryOutlined />; // Pending
    case "1":
      return <HourglassOutlined />; // Ongoing
    case "2":
      return <CheckCircleOutlined />; // Completed
    case "3":
      return <CloseCircleOutlined />; // Canceled
    case "4":
      return <FlagOutlined />; // Accepted
    default:
      return <></>;
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "0":
      return "Pending";
    case "1":
      return "Ongoing";
    case "2":
      return "Completed";
    case "3":
      return "Canceled";
    case "4":
      return "Accepted";
    default:
      return "Unknown";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "0":
      return "#ff9800"; // Pending - Orange
    case "1":
      return "#4caf50"; // Ongoing - Green
    case "2":
      return "#2196f3"; // Completed - Blue
    case "3":
      return "#f44336"; // Canceled - Red
    case "4":
      return "#faad14"; // Accepted - Light Green
    default:
      return "#ffffff"; // Default  color
  }
};
